import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, CalendarDays, Flag, Timer, Zap } from 'lucide-react';
import { dayjs, parsePlannedDate } from '../lib/utils';

type TrainingHeroProps = {
  racePlan: any;
  plannedWorkouts: any[];
};

const TrainingHero = ({ racePlan, plannedWorkouts }: TrainingHeroProps) => {
  const navigate = useNavigate();
  const raceDate = dayjs(racePlan.race_date || racePlan.race?.date);
  const trainingPlan = racePlan.training_plan || [];
  const workouts = trainingPlan.flatMap((week: any) => week.workouts || []);
  const completedCount = workouts.filter(
    (workout: any) => workout.completed,
  ).length;
  const progress = workouts.length
    ? Math.round((completedCount / workouts.length) * 100)
    : 0;
  const nextWorkout = [...plannedWorkouts]
    .filter(
      (workout) =>
        !workout.completed &&
        !parsePlannedDate(workout.planned_date).isBefore(dayjs(), 'day'),
    )
    .sort((first, second) =>
      parsePlannedDate(first.planned_date).diff(
        parsePlannedDate(second.planned_date),
      ),
    )[0];
  const daysUntilRace = raceDate.isValid()
    ? Math.max(raceDate.diff(dayjs(), 'days'), 0)
    : null;
  const raceName = racePlan.race_name || racePlan.race?.name || 'Goal race';

  return (
    <section className="training-hero">
      <div className="training-hero-grid" aria-hidden="true" />
      <div className="relative z-10 min-w-0">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-brand-volt">
          <Zap className="h-4 w-4" />
          Race readiness
        </div>
        <h2 className="mt-4 max-w-xl text-3xl font-bold leading-tight tracking-[-0.05em] text-white sm:text-4xl">
          Train for the day you want to have.
        </h2>
        <p className="mt-3 max-w-lg text-sm leading-6 text-white/65">
          Your plan is keeping the big picture in view. Stay consistent today,
          and let the work compound.
        </p>

        <div className="mt-7 flex flex-wrap gap-2">
          <button
            type="button"
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-brand-volt px-5 py-3 text-sm font-bold text-brand-midnight transition-transform hover:-translate-y-0.5"
            onClick={() => navigate({ to: '/plan' })}
          >
            Open training plan
            <ArrowRight className="h-4 w-4" />
          </button>
          {nextWorkout ? (
            <button
              type="button"
              className="inline-flex min-h-11 items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/15"
              onClick={() =>
                navigate({
                  to: '/plan/workout/$id',
                  params: { id: String(nextWorkout.id) },
                })
              }
            >
              View next session
            </button>
          ) : null}
        </div>
      </div>

      <div className="relative z-10 mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:mt-0 lg:grid-cols-2">
        <div className="training-hero-stat col-span-2 sm:col-span-1 lg:col-span-2">
          <span className="training-hero-stat-label">
            <Flag className="h-3.5 w-3.5" />
            {raceName}
          </span>
          <strong>{daysUntilRace === null ? '—' : daysUntilRace}</strong>
          <span className="text-xs text-white/55">days to go</span>
        </div>
        <div className="training-hero-stat">
          <span className="training-hero-stat-label">
            <CalendarDays className="h-3.5 w-3.5" />
            Plan
          </span>
          <strong>{progress}%</strong>
          <span className="text-xs text-white/55">complete</span>
        </div>
        <div className="training-hero-stat">
          <span className="training-hero-stat-label">
            <Timer className="h-3.5 w-3.5" />
            Next up
          </span>
          <strong className="truncate text-lg">
            {nextWorkout?.title || nextWorkout?.type || 'Rest and recover'}
          </strong>
          <span className="text-xs text-white/55">
            {nextWorkout
              ? parsePlannedDate(nextWorkout.planned_date).format('ddd, MMM D')
              : 'You are all caught up'}
          </span>
        </div>
      </div>
    </section>
  );
};

export default TrainingHero;
